import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";

import { fetchInitiativeDetail } from "../services/initiativesService";
import { InitiativePost } from "../types";
import { formatDisplayDate } from "../utils/formatDisplayDate";

const InitiativeDetail: React.FC = () => {
  const { slug: id } = useParams();
  const [initiative, setInitiative] = useState<InitiativePost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError("Initiative not found.");
      setIsLoading(false);
      return;
    }
    const loadDetail = async () => {
      try {
        const detail = await fetchInitiativeDetail(id);
        if (detail) {
          setInitiative(detail);
        } else {
          setError("Initiative not found.");
        }
      } catch (err) {
        console.error("Failed to fetch initiative detail", err);
        setError("Unable to load initiative detail right now.");
      } finally {
        setIsLoading(false);
      }
    };

    loadDetail();
  }, [id]);

  const tags = useMemo(() => {
    const sourceTags = initiative?.eng_tags;
    if (Array.isArray(sourceTags)) {
      return sourceTags
        .map((tag) => tag?.tag_name_eng)
        .filter((tagName): tagName is string => Boolean(tagName));
    }
    return [];
  }, [initiative]);

  const content =
    initiative?.eng_content ||
    initiative?.eng_description ||
    initiative?.eng_caption ||
    "";

  const publishedDate = formatDisplayDate(
    // Prefer created_at when available, then created_date, then published_at
    (initiative as any)?.created_at ||
      initiative?.created_date ||
      initiative?.published_at ||
      ""
  );

  // Add page-specific body class for template styles
  useEffect(() => {
    document.body.classList.add("page-initiative");
    return () => {
      document.body.classList.remove("page-initiative");
    };
  }, []);

  const pageBgStyle = useMemo(
    () => ({
      background: initiative?.eng_thumbnail
        ? `linear-gradient(135deg, rgba(0,51,102,.6) 0%, rgba(255,153,0,.35) 100%), url('${initiative.eng_thumbnail}') center/cover no-repeat`
        : undefined,
    }),
    [initiative]
  );

  if (isLoading) {
    return (
      <main id="main-content" role="main">
        <section
          className="page-title"
          style={pageBgStyle as React.CSSProperties}
        >
          <div className="container">
            <div className="page-title__inner">
              <div className="page-title__title-box">
                <h1 className="page-title__title">
                  {initiative?.eng_title || ""}
                </h1>
              </div>
              <p className="page-title__text">
                {initiative?.eng_caption || ""}
              </p>
            </div>
          </div>
        </section>

        <section className="blog-details" style={{ background: "#ffffff" }}>
          <div className="container">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading initiative...</span>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main id="main-content" role="main">
      {/* Page Title banner (template style) */}
      <section
        className="page-title"
        style={pageBgStyle as React.CSSProperties}
      >
        <div className="container">
          <div className="page-title__inner">
            <div className="page-title__title-box">
              <h1 className="page-title__title">
                {initiative?.eng_title || ""}
              </h1>
            </div>
            <p className="page-title__text">{initiative?.eng_caption || ""}</p>
          </div>
        </div>
      </section>

      {/* Content card (template blog-details structure) */}
      <section className="blog-details" style={{ background: "#ffffff" }}>
        <div className="container">
          {error && (
            <div className="alert alert-danger text-center" role="alert">
              {error}
            </div>
          )}
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="blog-details__content">
                <article className="rich-text-content blog-details__text">
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                    <div className="d-flex flex-column gap-2">
                      {tags.length > 0 && (
                        <div className="blog-details__tags d-flex gap-2 align-items-center flex-wrap">
                          {tags.map((tag, index) => (
                            <Link key={index} className="tag" to={`/tag/${encodeURIComponent(tag)}`}>
                              {tag}
                            </Link>
                          ))}
                        </div>
                      )}
                      {publishedDate && (
                        <span className="blog-card__date">
                          <i className="fas fa-calendar"></i> {publishedDate}
                        </span>
                      )}
                    </div>
                  </div>
                  {content ? (
                    <div
                      className="blog-details__text"
                      dangerouslySetInnerHTML={{ __html: content }}
                    />
                  ) : (
                    <p>No content available for this initiative.</p>
                  )}
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default InitiativeDetail;
